/** Keep authentication return targets on this origin and outside the login loop. */

function decodeUntilStable(value: string): string | null {
  let current = value;
  for (let i = 0; i < 5; i += 1) {
    let next = current;
    try {
      next = decodeURIComponent(current);
    } catch {
      return null;
    }
    if (next === current) return current;
    current = next;
  }
  return current;
}

function hasUnsafeChars(value: string): boolean {
  return /[\\\0\r\n\t\u2028\u2029]/.test(value);
}

export function safeReturnTo(value: string | null | undefined): string {
  const fallback = "/dashboard";
  if (!value) return fallback;
  const trimmed = value.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//") || hasUnsafeChars(trimmed)) return fallback;
  const decoded = decodeUntilStable(trimmed);
  if (
    !decoded
    || !decoded.startsWith("/")
    || decoded.startsWith("//")
    || decoded.includes("://")
    || decoded.includes("@")
    || hasUnsafeChars(decoded)
  ) {
    return fallback;
  }
  try {
    const target = new URL(decoded, window.location.origin);
    if (target.origin !== window.location.origin || target.username || target.password) return fallback;
    if (/^\/login(?:\/|$)/i.test(target.pathname)) return fallback;
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return fallback;
  }
}

const PAYFAST_CHECKOUT_HOSTS = new Set(["www.payfast.co.za", "sandbox.payfast.co.za"]);

export function safePayfastCheckoutUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed || hasUnsafeChars(trimmed) || trimmed.startsWith("//")) return null;
  const decoded = decodeUntilStable(trimmed);
  if (!decoded || decoded.startsWith("//") || hasUnsafeChars(decoded)) return null;
  try {
    // The decoded copy is only for the safety checks. Return the URL as given: decoding it first turns
    // %26/%3D inside a signed value (return_url=...?tab=billing%26payment=paid) into live separators,
    // which changes the fields PayFast receives and fails its signature check.
    for (const candidate of [new URL(decoded), new URL(trimmed)]) {
      if (candidate.protocol !== "https:" || candidate.username || candidate.password) return null;
      if (!PAYFAST_CHECKOUT_HOSTS.has(candidate.hostname.toLowerCase())) return null;
    }
    return new URL(trimmed).href;
  } catch {
    return null;
  }
}
